import React, {useMemo} from "react"
import debounce from "debounce-promise"
import {useQuery} from "@tanstack/react-query"
import {KlassDropAsyncPaginate, KlassDropdown} from "components/Select"
import {handleError} from "helpers"
import {majorService} from "services"

type SpecializationOption = {
    specializationId: number
    name: string
}

type Props = {
    programVersionIds?: number[]
    [key: string]: any
}

export function SpecializationSelect({programVersionIds = [], ...props}: Props) {
    const {data: versionSpecializations = [], isFetching} = useQuery(
        ["student-filter-program-version-specializations", programVersionIds],
        async () => {
            try {
                const versions = await Promise.all(
                    programVersionIds.map((id) => majorService.getMajorVersionDetail(id))
                )
                const byId = new Map<number, SpecializationOption>()
                versions.forEach((version) => {
                    ;(version.specializations || []).forEach((item: SpecializationOption) => {
                        if (item?.specializationId) {
                            byId.set(item.specializationId, item)
                        }
                    })
                })
                return Array.from(byId.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            } catch (error) {
                handleError(error)
                return []
            }
        },
        {enabled: programVersionIds.length > 0, initialData: []}
    )

    const loadSpecializations = useMemo(
        () =>
            debounce(async (search: string, loadedOptions) => {
                try {
                    const pageSize = 20
                    const page = Math.ceil(loadedOptions.length / pageSize) + 1
                    const {data, total} = await majorService.getProgramSpecialization({
                        filter: {search: search ?? ""},
                        range: {page, pageSize}
                    })
                    return {options: data ?? [], hasMore: loadedOptions.length < (total ?? 0)}
                } catch (error) {
                    handleError(error)
                    return {options: [], hasMore: false}
                }
            }, 300),
        []
    )

    if (programVersionIds.length) {
        return (
            <KlassDropdown
                isMulti
                isClearable
                options={versionSpecializations}
                valueKey="specializationId"
                isLoading={isFetching}
                {...props}
            />
        )
    }

    return (
        <KlassDropAsyncPaginate
            isMulti
            isClearable
            valueKey="specializationId"
            loadOptions={loadSpecializations}
            {...props}
        />
    )
}
